import _ from 'lodash';
import React from 'react';

import {
  Box,
  Button,
  Grid,
  IconButton,
  styled
} from '@mui/material';
import {
  BaseModal,
  Form
} from 'iron-forge-react-modules';
import {
  useForm,
  useWatch
} from 'react-hook-form';

import { Close } from '@mui/icons-material';
import { selectFields } from 'modules';
import * as dataService from './dataService';
import * as types from './types';



const { genController: genUserRoleTemplateSelectFieldController } = selectFields.UserRoleTemplateSelectField.genComponentAndControllerGenerator();
const StoreSelectField = selectFields.StoreSelectField.genComponentAndControllerGenerator();
const ControlledUserRoleTemplateSelectField = genUserRoleTemplateSelectFieldController<types.IFormState>();
const ControlledStoreSelectField = StoreSelectField.genController<types.IFormState>();



const ButtonBox = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between'
}));

const FormContainerBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  gap: theme.spacing(1)
}));



export interface IProps {
  siteUserId: string;
  onClose: () => void;
}

export const FC: React.FC<IProps> = props => {
  const queryTuple = dataService.useQuery(props.siteUserId);
  const [assignRole, assignRoleTuple] = dataService.useAssignRole(props.siteUserId);
  const form = useForm<types.IFormState>({
    defaultValues: {
      roleTemplate: null,
      stores: []
    }
  });

  const watch = useWatch({ control: form.control });
  const selectedRoleIsGlobal = React.useMemo(
    () => !watch.roleTemplate || watch.roleTemplate?.visibilityScopeName === 'global',
    [watch]
  );



  const processSubmit = React.useCallback(
    (formState: types.IFormState) => {
      if (!formState.roleTemplate) {
        return;
      }
      const assignedStoreIds = queryTuple.data.roleTemplateId2AssignedStoreIds[formState.roleTemplate.id];

      if (
        !_.isEmpty(formState.stores) &&
        _.some(formState.stores, store => _.includes(assignedStoreIds, store.id))
      ) {
        alert("This role is already assigned to a selected store.");
        return;

      } else if (
        _.isEmpty(formState.stores) &&
        _.has(queryTuple.data.roleTemplateId2AssignedStoreIds, formState.roleTemplate.id)
      ) {
        alert("This role is already assigned to this user.");
        return;
      }

      assignRole(formState);
      props.onClose();
    },
    [
      assignRole,
      props,
      queryTuple.data.roleTemplateId2AssignedStoreIds
    ]
  );

  const content = React.useMemo(
    () => (
      <Form.FC onSubmit={form.handleSubmit(processSubmit)}>
        <FormContainerBox>
          <Grid
            container
            spacing={2}>
            <Grid
              item
              xs={12}>
              <ControlledUserRoleTemplateSelectField
                isDisabled={assignRoleTuple.loading}
                label="Role"
                formControl={form.control}
                fieldKey="roleTemplate"
                isRequired />
            </Grid>

            <Grid
              item
              xs={12}>
              {!selectedRoleIsGlobal && (
                <ControlledStoreSelectField
                  fieldKey="stores"
                  selectMultiple
                  isRequired
                  formControl={form.control} />
              )}
            </Grid>

            <Grid
              item
              xs={12}>
              <ButtonBox>
                <Button
                  disabled={assignRoleTuple.loading}
                  onClick={props.onClose}>
                  Go Back
                </Button>

                <Button
                  disabled={assignRoleTuple.loading}
                  type="submit"
                  variant="contained">
                  Submit
                </Button>
              </ButtonBox>
            </Grid>
          </Grid>
        </FormContainerBox>
      </Form.FC>
    ),
    [
      assignRoleTuple.loading,
      form,
      processSubmit,
      props.onClose,
      selectedRoleIsGlobal
    ]);



  const fnCloseButton: BaseModal.IProps['fnGenElTopCloseButton'] = ({ onClose }) => (
    <IconButton onClick={onClose}>
      <Close />
    </IconButton>
  );



  return (
    <BaseModal.FC
      title="Assign Role"
      isOpen
      fnGenElTopCloseButton={fnCloseButton}
      fnGenElCloseButton={fnCloseButton}
      onClose={props.onClose}>
      {() => content}
    </BaseModal.FC>
  );
};
